/**
 * Utility functions for exporting data to CSV format
 */

/**
 * Converts an array of objects to CSV format
 * @param data Array of objects to convert
 * @param columns Optional array of column definitions. If not provided, all keys from first object are used
 * @returns CSV string
 */
export function convertToCSV<T extends Record<string, any>>(
  data: T[],
  columns?: { key: keyof T; label: string }[]
): string {
  if (!data || data.length === 0) {
    return '';
  }

  // If columns not provided, use all keys from first object
  const columnsToUse = columns || Object.keys(data[0]).map(key => ({
    key: key as keyof T,
    label: key
  }));

  // Create header row
  const header = columnsToUse.map(col => escapeCSVValue(col.label)).join(',');

  // Create data rows
  const rows = data.map(row => {
    return columnsToUse.map(col => {
      const value = row[col.key];
      return escapeCSVValue(formatValue(value));
    }).join(',');
  });

  return [header, ...rows].join('\n');
}

/**
 * Escapes special characters in CSV values
 * @param value Value to escape
 * @returns Escaped value
 */
function escapeCSVValue(value: string): string {
  if (value === null || value === undefined) {
    return '';
  }

  const stringValue = String(value);

  // If value contains comma, newline, or double quote, wrap in quotes and escape quotes
  if (stringValue.includes(',') || stringValue.includes('\n') || stringValue.includes('"')) {
    return `"${stringValue.replace(/"/g, '""')}"`;
  }

  return stringValue;
}

/**
 * Formats a value for CSV export
 * @param value Value to format
 * @returns Formatted string
 */
function formatValue(value: any): string {
  if (value === null || value === undefined) {
    return '';
  }

  // Handle dates
  if (value instanceof Date) {
    return value.toISOString();
  }

  // Handle booleans
  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }

  // Handle arrays
  if (Array.isArray(value)) {
    return value.join('; ');
  }

  // Handle objects
  if (typeof value === 'object') {
    return JSON.stringify(value);
  }

  return String(value);
}

/**
 * Downloads a CSV file
 * @param csvContent CSV content string
 * @param fileName File name without extension
 */
export function downloadCSV(csvContent: string, fileName: string): void {
  // Create a Blob from the CSV content
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });

  // Create a download link
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);

  link.setAttribute('href', url);
  link.setAttribute('download', `${fileName}.csv`);
  link.style.visibility = 'hidden';

  // Append to body, click, and remove
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  // Clean up the URL object
  URL.revokeObjectURL(url);
}

/**
 * Exports data to CSV and triggers download
 * @param data Array of objects to export
 * @param fileName File name without extension
 * @param columns Optional array of column definitions
 */
export function exportToCSV<T extends Record<string, any>>(
  data: T[],
  fileName: string,
  columns?: { key: keyof T; label: string }[]
): void {
  const csvContent = convertToCSV(data, columns);
  downloadCSV(csvContent, fileName);
}
